<script setup>
/**
 * AgentShoppingActions.vue  (agent chat-window shopping actions - Tyler 2026-07-01)
 *
 * Three agent write-actions on a product the customer is viewing/has viewed, calling the token-gated
 * Nextlevel_ChatContext agent endpoints (/chatcontext/agent/{setcatalogprice,createcoupon,addproduct}):
 *   1. Set catalog price  - two-step preview -> confirm, margin-guarded.
 *   2. Make coupon         - margin-safe, per-customer (email-scoped); over-floor auto-routes to approval.
 *   3. Add product from URL - scrape -> create (reuses the UrlProductAdd pipeline).
 * Auth: X-Agent-Token (cfg.overrideToken) + email, origin-pinned server-side.
 */
import { ref, computed } from 'vue';

const props = defineProps({
  baseUrl: { type: String, default: '' },
  agentToken: { type: String, default: '' },
  email: { type: String, default: '' },
  conversationId: { type: [String, Number], default: '' },
  products: { type: Array, default: () => [] }, // [{sku,name,price}]
});

const open = ref('');
const toggle = k => { open.value = open.value === k ? '' : k; };
// Actions render when the agent context (baseUrl + token) is present. Email is required only for the
// customer-scoped coupon action, which validates it at click-time; price/add-product do not need it.
// Render when we have a conversation. Auth is the Chatwoot session (BFF); no browser token needed.
const ready = computed(() => !!props.conversationId);

// BFF write (Option B): same-origin, session-authed via window.axios. Server resolves email + holds the
// Magento write token; the browser sends NO token. idempotency_key dedupes double-applies server-side.
const routeAccountId = () => String((window.location.pathname.match(/\/accounts\/(\d+)/) || [])[1] || '');
const mkIdem = () => 'nlbff-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
const post = async (action, params, idem) => {
  const aid = routeAccountId();
  const payload = { proxy_action: action, conversation_id: String(props.conversationId || ''), ...params };
  if (idem) payload.idempotency_key = idem;
  const { data } = await window.axios.post(`/api/v1/accounts/${aid}/nextlevel/agent/write`, payload);
  return data;
};

// ---- 1. catalog price ----
const priceSku = ref(''); const newPrice = ref(''); const pricePreview = ref(null); const priceMsg = ref(''); const priceBusy = ref(false);
const previewPrice = async () => {
  priceMsg.value = ''; pricePreview.value = null;
  if (!priceSku.value || !(parseFloat(newPrice.value) > 0)) { priceMsg.value = 'Pick a product and enter a price.'; return; }
  priceBusy.value = true;
  try {
    const r = await post('setcatalogprice', { sku: priceSku.value, new_price: newPrice.value });
    if (r.ok && r.preview) pricePreview.value = r; else priceMsg.value = r.error || 'preview failed';
  } finally { priceBusy.value = false; }
};
const applyPrice = async override => {
  priceBusy.value = true; priceMsg.value = '';
  try {
    const r = await post('setcatalogprice', { sku: priceSku.value, new_price: newPrice.value, confirm: '1', margin_override: override ? '1' : '0' }, mkIdem());
    if (r.ok && r.applied) { priceMsg.value = `Updated ${r.sku}: $${r.before} -> $${r.after}`; pricePreview.value = null; }
    else if (r.error === 'below_margin_floor') { priceMsg.value = `Below margin floor ($${r.margin_floor}). Confirm override to force.`; pricePreview.value = { ...pricePreview.value, needs_margin_override: true }; }
    else priceMsg.value = r.error || 'apply failed';
  } finally { priceBusy.value = false; }
};

// ---- 2. coupon ----
const cSku = ref(''); const cMode = ref('percent'); const cAmt = ref(''); const cMsg = ref(''); const cBusy = ref(false);
const makeCoupon = async () => {
  cMsg.value = '';
  if (!cSku.value) { cMsg.value = 'Pick a product.'; return; }
  const p = { sku: cSku.value, mode: cMode.value };
  if (cMode.value === 'fixed') p.amount_off = cAmt.value;
  if (cMode.value === 'percent') p.percent_off = cAmt.value;
  cBusy.value = true;
  try {
    const r = await post('createcoupon', p, mkIdem());
    const res = r.result || {};
    if (res.code) cMsg.value = `Coupon minted: ${res.code}`;
    else if (res.outcome === 'pending' || res.pending_id) cMsg.value = `Sent for approval (over margin floor). Pending #${res.pending_id}.`;
    else cMsg.value = res.error || r.error || 'coupon failed';
  } finally { cBusy.value = false; }
};

// ---- 3. add product from URL ----
const url = ref(''); const addMsg = ref(''); const addBusy = ref(false); const scraped = ref(null);
const scrapeUrl = async () => {
  addMsg.value = 'Scraping...'; scraped.value = null;
  if (!url.value) { addMsg.value = 'Enter a URL.'; return; }
  addBusy.value = true;
  try {
    const q = await post('addproduct', { action: 'scrape', url: url.value });
    if (q.status !== 'queued') { addMsg.value = q.error || 'could not queue'; addBusy.value = false; return; }
    let tries = 0;
    const poll = setInterval(async () => {
      if (++tries > 90) { clearInterval(poll); addMsg.value = 'Timed out.'; addBusy.value = false; return; }
      const s = await post('addproduct', { action: 'status', request_id: q.request_id });
      if (s.request_status === 'complete') {
        clearInterval(poll); addBusy.value = false;
        const pl = s.payload || {};
        if (!pl || pl.status === 'failed') { addMsg.value = 'Scrape failed: ' + (pl.reason || '?'); return; }
        scraped.value = pl; addMsg.value = `Scraped: ${pl.name || ''} (${pl.brand_name || ''}) $${pl.price || ''}`;
      } else if (s.request_status === 'error') { clearInterval(poll); addBusy.value = false; addMsg.value = 'Error: ' + (s.reason || '?'); }
    }, 3000);
  } catch (e) { addBusy.value = false; addMsg.value = 'error'; }
};
const createScraped = async () => {
  if (!scraped.value) return;
  addBusy.value = true; addMsg.value = 'Creating...';
  try {
    const r = await post('addproduct', { action: 'create', payload_json: JSON.stringify(scraped.value), image_mode: 'as_is' }, mkIdem());
    if (r.status === 'ok' || r.status === 'updated') { addMsg.value = `Product ${r.status}: ${r.sku}`; scraped.value = null; url.value = ''; }
    else if (r.status === 'duplicate') { addMsg.value = 'Already on store - use the admin panel to update/confirm.'; }
    else addMsg.value = r.reason || r.error || 'create failed';
  } finally { addBusy.value = false; }
};
</script>

<template>
  <div v-if="ready" class="ne-shop-actions px-4 py-3 border-b border-n-weak">
    <div class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10 mb-1.5">Agent Actions</div>

    <!-- 1. price -->
    <button @click="toggle('price')" class="ne-act">Set catalog price</button>
    <div v-if="open==='price'" class="ne-body">
      <select v-model="priceSku"><option value="">Select product...</option>
        <option v-for="p in products" :key="p.sku" :value="p.sku">{{ p.name || p.sku }} ({{ p.sku }})</option></select>
      <input v-model="newPrice" type="number" step="0.01" placeholder="New price $" />
      <button :disabled="priceBusy" @click="previewPrice">Preview</button>
      <div v-if="pricePreview" class="ne-note">
        {{ pricePreview.sku }}: ${{ pricePreview.current_price }} -> ${{ pricePreview.new_price }} ({{ pricePreview.delta_pct }}%)
        <span v-if="pricePreview.margin_floor > 0"> - margin floor ${{ pricePreview.margin_floor }}, {{ pricePreview.margin_ok ? 'OK' : 'BELOW' }}</span>
        <div>
          <button :disabled="priceBusy" @click="applyPrice(false)">Confirm & apply</button>
          <button v-if="pricePreview.needs_margin_override" :disabled="priceBusy" @click="applyPrice(true)" style="color:#a00;">Override margin & apply</button>
        </div>
      </div>
      <div v-if="priceMsg" class="ne-msg">{{ priceMsg }}</div>
    </div>

    <!-- 2. coupon -->
    <button @click="toggle('coupon')" class="ne-act">Make coupon</button>
    <div v-if="open==='coupon'" class="ne-body">
      <select v-model="cSku"><option value="">Select product...</option>
        <option v-for="p in products" :key="p.sku" :value="p.sku">{{ p.name || p.sku }} ({{ p.sku }})</option></select>
      <select v-model="cMode"><option value="percent">Percent %</option><option value="fixed">Fixed $</option><option value="auto">Auto (margin-max)</option></select>
      <input v-if="cMode!=='auto'" v-model="cAmt" type="number" step="0.01" :placeholder="cMode==='percent' ? '% off' : '$ off'" />
      <button :disabled="cBusy" @click="makeCoupon">Create coupon</button>
      <div v-if="cMsg" class="ne-msg">{{ cMsg }}</div>
    </div>

    <!-- 3. add product -->
    <button @click="toggle('add')" class="ne-act">Add product from URL</button>
    <div v-if="open==='add'" class="ne-body">
      <input v-model="url" type="url" placeholder="https://manufacturer.com/product" />
      <button :disabled="addBusy" @click="scrapeUrl">Scrape</button>
      <button v-if="scraped" :disabled="addBusy" @click="createScraped">Add to store</button>
      <div v-if="addMsg" class="ne-msg">{{ addMsg }}</div>
    </div>
  </div>
</template>

<style scoped>
/* Quiet secondary actions, one card language, n-slate neutral. Only the margin-override (destructive)
   carries a red hint (inline). Nothing competes with the LIVE amber accent. */
.ne-act { display:block; width:100%; text-align:left; font-size:12px; padding:5px 8px; margin:3px 0; border:1px solid rgba(120,130,150,0.28); border-radius:6px; background:transparent; color:inherit; cursor:pointer; transition:background-color .12s; }
.ne-act:hover { background:rgba(120,130,150,0.08); }
.ne-body { padding:6px 8px; background:rgba(120,130,150,0.05); border-radius:6px; margin-bottom:4px; }
.ne-body select, .ne-body input { display:block; width:100%; margin:3px 0; font-size:12px; padding:4px 6px; border:1px solid rgba(120,130,150,0.28); border-radius:5px; background:transparent; color:inherit; }
.ne-body button { font-size:12px; padding:4px 10px; margin:3px 4px 0 0; border:1px solid rgba(120,130,150,0.35); border-radius:5px; background:transparent; color:inherit; cursor:pointer; transition:background-color .12s; }
.ne-body button:hover { background:rgba(120,130,150,0.1); }
.ne-note { font-size:11px; margin:4px 0; padding:5px; background:rgba(120,130,150,0.06); border:1px solid rgba(120,130,150,0.18); border-radius:5px; }
.ne-msg { font-size:11px; opacity:0.85; margin-top:4px; }
</style>
